/**
 * PortalPage — Main landing page showing business units as expandable accordion rows.
 * Each BU row has "Manage" and "Add new client" actions.
 */

import { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Spinner, Alert } from "react-bootstrap";
import { useBusinessUnits } from "../hooks/useBusinessUnits";
import { api } from "../utils/api";
import "./portal.css";

interface ClientItem {
  clientId: string;
  name: string;
  webName: string;
  logoUrl?: string;
}

interface EventItem {
  eventId: string;
  clientId: string;
  name: string;
  userCount: number;
}

interface ClientsResponse {
  clients: ClientItem[];
  count: number;
}

interface EventsResponse {
  events: EventItem[];
  count: number;
}

/** Sample BU names matching the reference design for when API returns empty */

export default function PortalPage() {
  const { businessUnits, loading, error } = useBusinessUnits();
  const [searchParams, setSearchParams] = useSearchParams();

  // Expanded state — persisted to localStorage
  const EXPANDED_KEY = "anyplay-portal-expanded";
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem(EXPANDED_KEY);
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch { return new Set(); }
  });

  // Sort option
  const [sortOrder, setSortOrder] = useState<"az" | "za">("az");

  const [clientsCache, setClientsCache] = useState<Record<string, ClientItem[]>>({});
  const [clientsLoading, setClientsLoading] = useState<string | null>(null);
  const [eventsCache, setEventsCache] = useState<Record<string, EventItem[]>>({});
  const [eventsLoading, setEventsLoading] = useState<Set<string>>(new Set());

  // Persist expanded state
  const persistExpanded = (ids: Set<string>) => {
    try { localStorage.setItem(EXPANDED_KEY, JSON.stringify([...ids])); } catch {}
  };

  // Auto-expand BU when navigating back from viewer with expandClient param
  useEffect(() => {
    const expandClientId = searchParams.get("expandClient");
    if (!expandClientId) return;

    // Remove the param from URL without adding a history entry
    searchParams.delete("expandClient");
    setSearchParams(searchParams, { replace: true });

    // Fetch the client to find its businessUnitId, then expand that BU
    api.get<{ client: { clientId: string; businessUnitId: string } }>(`/api/clients/${expandClientId}`)
      .then((data) => {
        const buId = data.client.businessUnitId;
        setExpandedIds((prev) => {
          const next = new Set(prev);
          next.add(buId);
          persistExpanded(next);
          return next;
        });
        // Fetch clients for the expanded BU if not cached
        if (!clientsCache[buId]) {
          fetchClientsForBU(buId);
        }
      })
      .catch(() => { /* silently ignore if client not found */ });
  }, []);

  // Sort BUs
  const displayUnits = [...businessUnits].sort((a, b) => {
    return sortOrder === "az" ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
  });

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); } else { next.add(id); }
      persistExpanded(next);
      return next;
    });

    // Fetch clients for this BU if not cached
    if (!expandedIds.has(id) && !clientsCache[id]) {
      fetchClientsForBU(id);
    }
  };

  const expandAll = () => {
    const allIds = new Set(businessUnits.map((bu) => bu.id));
    setExpandedIds(allIds);
    persistExpanded(allIds);
    // Fetch all uncached
    for (const bu of businessUnits) {
      if (!clientsCache[bu.id]) fetchClientsForBU(bu.id);
    }
  };

  const collapseAll = () => {
    setExpandedIds(new Set());
    persistExpanded(new Set());
  };

  const fetchClientsForBU = async (buId: string) => {
    setClientsLoading(buId);
    try {
      const data = await api.get<ClientsResponse>(`/api/clients?businessUnitId=${buId}`);
      setClientsCache((prev) => ({ ...prev, [buId]: data.clients }));
      // Also fetch events for each client
      for (const client of data.clients) {
        fetchEventsForClient(client.clientId);
      }
    } catch {
      setClientsCache((prev) => ({ ...prev, [buId]: [] }));
    } finally {
      setClientsLoading(null);
    }
  };

  // Auto-fetch clients for BUs that are already expanded from localStorage on mount
  useEffect(() => {
    if (businessUnits.length === 0) return;
    for (const buId of expandedIds) {
      if (!clientsCache[buId]) {
        fetchClientsForBU(buId);
      }
    }
  }, [businessUnits.length]);

  const fetchEventsForClient = async (clientId: string) => {
    setEventsLoading((prev) => new Set(prev).add(clientId));
    try {
      const data = await api.get<EventsResponse>(`/api/events?clientId=${clientId}`);
      setEventsCache((prev) => ({ ...prev, [clientId]: data.events }));
    } catch {
      setEventsCache((prev) => ({ ...prev, [clientId]: [] }));
    } finally {
      setEventsLoading((prev) => {
        const next = new Set(prev);
        next.delete(clientId);
        return next;
      });
    }
  };

  if (loading) {
    return (
      <div className="portal-loading">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <div className="portal-container">
      <div className="portal-card">
        {/* Header */}
        <div className="portal-header">
          <span className="portal-header-title">Portal</span>
        </div>

        {/* Toolbar — sort and expand/collapse */}
        <div className="portal-toolbar">
          <div className="portal-toolbar-left">
            <select
              className="portal-sort-select"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value as "az" | "za")}
              title="Sort order"
            >
              <option value="az">A → Z</option>
              <option value="za">Z → A</option>
            </select>
          </div>
          <div className="portal-toolbar-right">
            <button className="portal-toolbar-btn" onClick={expandAll} title="Expand all">
              Expand all
            </button>
            <button className="portal-toolbar-btn" onClick={collapseAll} title="Collapse all">
              Collapse all
            </button>
          </div>
        </div>

        {error && <Alert variant="danger" className="mx-3">{error}</Alert>}

        {/* Business Unit List */}
        <div className="portal-bu-list">
          {displayUnits.map((bu) => (
            <div key={bu.id} className="portal-bu-row">
              <div className="portal-bu-row-header">
                <button
                  className="portal-bu-toggle"
                  onClick={() => toggleExpand(bu.id)}
                  aria-expanded={expandedIds.has(bu.id)}
                >
                  <svg
                    className={`portal-bu-chevron ${expandedIds.has(bu.id) ? "expanded" : ""}`}
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                  <span className="portal-bu-name">{bu.name}</span>
                </button>
                <div className="portal-bu-actions">
                  <a href={`/admin/business-units/${bu.id}`} className="portal-bu-action">
                    Manage
                  </a>
                  <a href={`/admin/business-units/${bu.id}/clients/new`} className="portal-bu-action">
                    Add new client
                  </a>
                </div>
              </div>

              {/* Expanded content — show clients */}
              {expandedIds.has(bu.id) && (
                <div className="portal-bu-expanded">
                  {clientsLoading === bu.id ? (
                    <Spinner animation="border" size="sm" variant="primary" />
                  ) : (clientsCache[bu.id] || []).length > 0 ? (
                    <ul className="portal-client-list">
                      {(clientsCache[bu.id] || []).map((client) => (
                        <li key={client.clientId} className="portal-client-item-group">
                          <div className="portal-client-item">
                            {client.logoUrl && (
                              <img src={client.logoUrl} alt="" className="portal-client-logo" />
                            )}
                            <span className="portal-client-name-text">
                              {client.name}
                            </span>
                            <Link
                              to={`/admin/business-units/${bu.id}/clients/${client.clientId}`}
                              className="portal-client-manage"
                            >
                              Manage
                            </Link>
                          </div>
                          {/* Events under this client */}
                          <div className="portal-events-list">
                            {eventsLoading.has(client.clientId) ? (
                              <Spinner animation="border" size="sm" variant="secondary" />
                            ) : (eventsCache[client.clientId] || []).length > 0 ? (
                              (eventsCache[client.clientId] || []).map((event) => (
                                <div key={event.eventId} className="portal-event-item">
                                  <Link
                                    to={`/viewer?eventId=${event.eventId}`}
                                    className="portal-event-name"
                                  >
                                    {event.name}
                                  </Link>
                                  <Link
                                    to={`/admin/business-units/${bu.id}/clients/${client.clientId}/events/${event.eventId}`}
                                    className="portal-event-manage"
                                  >
                                    Manage
                                  </Link>
                                </div>
                              ))
                            ) : null}
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="portal-bu-empty">No clients in this business unit.</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Add new business unit link */}
        <div className="portal-add-bu">
          <a href="/admin/business-units" className="portal-bu-action">
            Add new business unit
          </a>
        </div>
      </div>

      {/* Footer */}
      <footer className="portal-footer">
        &copy; {new Date().getFullYear()} NEP Group, Inc. | NEP Connected Solutions |{" "}
        <a href="/privacy" className="portal-footer-link">Privacy Policy</a>
      </footer>
    </div>
  );
}
